import { UserFields } from 'src/constants';
import { Tag } from 'src/store/tags/types';

interface FilterAllowedTagIdSetForUserInput {
  tags: Tag[];
  userId: string;
  userFields: UserFields;
  activeCompanyCustomFields?: Record<string, any>;
}

// Multi-select values are string lists and a select value is one option id string.
const addTagValues = (target: Record<string, boolean>, value: unknown) => {
  const items = Array.isArray(value) ? value : [value];
  items.forEach((item) => {
    if (typeof item === 'string') {
      target[item] = true;
    }
  });
};

export const FilterAllowedTagIdSetForUser = (
  input: FilterAllowedTagIdSetForUserInput,
) => {
  const { customFields } = input.userFields;
  // create a set of custom fields values for the client
  const currentUserTagCustomFieldValues: Record<string, boolean> = {};
  if (customFields) {
    Object.values(customFields).forEach((customFieldOptions) => {
      addTagValues(currentUserTagCustomFieldValues, customFieldOptions);
    });
  }

  // create a set of custom field values for the active company
  const currentCompanyCustomFieldValues: Record<string, boolean> = {};
  if (input.activeCompanyCustomFields) {
    Object.values(input.activeCompanyCustomFields).forEach(
      (customFieldOptions) => {
        addTagValues(currentCompanyCustomFieldValues, customFieldOptions);
      },
    );
  }

  return input.tags
    .filter((tag) => {
      const tagVisibility = tag.structFields.visibility;
      // when there is no visibility filtering or no filter id values it is always allowed
      if (!tagVisibility || !tagVisibility.filterIDValues) {
        return true;
      }

      switch (tagVisibility.filterType) {
        case 'client': {
          // when the current user is in the list of allowed clients, it is allowed
          return tagVisibility.filterIDValues?.includes(input.userId);
        }
        case 'company': {
          // when the current user is in the list of allowed companies, it is allowed
          const { companyId } = input.userFields;
          if (!companyId) {
            return false;
          }
          return tagVisibility.filterIDValues?.includes(companyId);
        }
        case 'customField': {
          // when the current user has a value for the tag custom field, it is allowed
          if (!customFields) {
            return false;
          }
          // find if any tag visibility values are in any tag custom fields values on this client
          const matchedCustomFieldValue = tagVisibility.filterIDValues?.find(
            (tagCustomFieldValue) =>
              currentUserTagCustomFieldValues[tagCustomFieldValue],
          );
          return Boolean(matchedCustomFieldValue);
        }
        case 'companyCustomField': {
          // when the active company has a value for the tag custom field, it is allowed
          if (!input.activeCompanyCustomFields) {
            return false;
          }
          // find if any tag visibility values are in any company custom fields values
          const matchedCompanyCustomFieldValue =
            tagVisibility.filterIDValues?.find(
              (tagCustomFieldValue) =>
                currentCompanyCustomFieldValues[tagCustomFieldValue],
            );
          return Boolean(matchedCompanyCustomFieldValue);
        }
        default:
          return false;
      }
    })
    .reduce(
      (acc, tag) => {
        acc[tag.id] = true;
        return acc;
      },
      {} as Record<string, boolean>,
    );
};
